import qs from "qs";
import { IGetAggregatedDataQuery } from "types/aggregateData";

import { IGetClientsQuery, IGetClientStatementsReportsQuery } from "../types/clients";
import { IFilter, ISortBy } from "../types/common";
import { IGetInviteTokensQuery, IGetModulesDictionaryQuery } from "../types/inviteTokens";
import { IGetProjectsQuery } from "../types/projects";
import { IGetUsersQuery } from "../types/users";

export const withPagination = (pageIndex: number, pageSize: number) => {
    return {
        page: pageIndex,
        limit: pageSize,
    };
};

export const withGlobalFilter = (global: string) => {
    return {
        global: global,
    };
};

export const withFilters = (filters?: IFilter[]) => {
    return {
        filters,
    };
};

export const withSortBy = (sortBy?: ISortBy[]) => {
    return {
        ...(sortBy?.[0] && {
            sort: sortBy[0].id,
            desc: sortBy[0].desc ? "desc" : "asc",
        }),
    };
};

export const getUsersQueryString = ({
    pageIndex = 1,
    pageSize = 20,
    globalFilter = "",
    roles = undefined,
    projectId = undefined,
    organizationId = undefined,
    includeManagersToPersonnelSelector = undefined,
    enabled = undefined,
}: IGetUsersQuery) => {
    return qs.stringify(
        {
            ...withPagination(pageIndex, pageSize),
            ...withGlobalFilter(globalFilter),
            ...(roles !== undefined && {
                roles,
            }),
            ...(projectId !== undefined && {
                projectId,
            }),
            ...(organizationId !== undefined && {
                organizationId,
            }),
            ...(includeManagersToPersonnelSelector !== undefined && {
                includeManagersToPersonnelSelector,
            }),
            ...(enabled !== undefined && {
                enabled,
            }),
        },
        { encode: false }
    );
};

export const getClientsQueryString = ({
    pageIndex = 1,
    pageSize = 20,
    globalFilter = "",
    projectId = undefined,
    referId = undefined,
    hasNewMessage = undefined,
    hasNewNotes = undefined,
    hasSuicideAlert = undefined,
    hasAssessmentAlert = undefined,
    hasPartnerReports = undefined,
    assessment = undefined,
    personnelId = undefined,
    activityCategory = undefined,
    lastSeen = undefined,
    createdWithin = undefined,
    visibleClients = undefined,
    myTokensOnly = undefined,
    sort = undefined,
    desc = undefined,
}: IGetClientsQuery) => {
    return qs.stringify(
        {
            ...withPagination(pageIndex, pageSize),
            ...withGlobalFilter(globalFilter),
            ...(projectId !== undefined && {
                projectId,
            }),
            ...(referId !== undefined && {
                referId,
            }),
            ...(assessment !== undefined && {
                assessment,
            }),
            ...(personnelId !== undefined && {
                personnelId,
            }),
            ...(activityCategory !== undefined &&
                activityCategory.length > 0 && {
                    activityCategory,
                }),
            ...(lastSeen !== undefined && {
                lastSeen,
            }),
            ...(createdWithin !== undefined && {
                createdWithin,
            }),
            ...(hasNewMessage !== undefined && {
                hasNewMessage,
            }),
            ...(hasNewNotes !== undefined && {
                hasNewNotes,
            }),
            ...(hasSuicideAlert !== undefined && {
                hasSuicideAlert,
            }),
            ...(hasAssessmentAlert !== undefined && {
                hasAssessmentAlert,
            }),
            ...(hasPartnerReports !== undefined && {
                hasPartnerReports,
            }),
            ...(visibleClients !== null &&
                visibleClients !== undefined && {
                    visibleClients,
                }),
            ...(myTokensOnly !== undefined && {
                myTokensOnly,
            }),
            ...(projectId === null && {
                projectId: "null",
            }),
            ...(sort !== undefined &&
                desc !== undefined && {
                    sort,
                    desc,
                }),
        },
        { encode: false }
    );
};

export const getClientStatementsReportsQueryString = ({
    trackId = undefined,
    moduleCode = undefined,
    exerciseCode = undefined,
}: IGetClientStatementsReportsQuery) => {
    return qs.stringify(
        {
            ...(trackId !== undefined && {
                trackId,
            }),
            ...(moduleCode !== undefined && {
                moduleCode,
            }),
            ...(exerciseCode !== undefined && {
                exerciseCode,
            }),
        },
        { encode: false }
    );
};

export const getProjectsQueryString = ({ pageIndex = 1, pageSize = 20, globalFilter = "", organizationId = undefined, pinned = undefined }: IGetProjectsQuery) => {
    return qs.stringify(
        {
            ...withPagination(pageIndex, pageSize),
            ...withGlobalFilter(globalFilter),
            ...(organizationId !== undefined && {
                organizationId,
            }),
            ...(pinned !== undefined && {
                pinned,
            }),
        },
        { encode: false }
    );
};

export const getOrganizationsQueryString = ({ pageIndex = 1, pageSize = 20, globalFilter = "" }: IGetProjectsQuery) => {
    return qs.stringify(
        {
            ...withPagination(pageIndex, pageSize),
            ...withGlobalFilter(globalFilter),
        },
        { encode: false }
    );
};

export const getInviteTokensQueryString = ({
    pageIndex = 1,
    pageSize = 20,
    globalFilter = "",
    projectId,
    createdBy,
    personnelId,
    visibleClients = undefined,
    myTokensOnly = undefined,
}: IGetInviteTokensQuery) => {
    return qs.stringify(
        {
            ...withPagination(pageIndex, pageSize),
            ...withGlobalFilter(globalFilter),
            ...(projectId !== undefined && {
                projectId,
            }),
            ...(createdBy !== undefined && {
                createdBy,
            }),
            ...(personnelId !== undefined && {
                personnelId,
            }),
            ...(visibleClients !== null &&
                visibleClients !== undefined && {
                    visibleClients,
                }),
            ...(myTokensOnly !== undefined && {
                myTokensOnly,
            }),
        },
        { encode: false }
    );
};
export const getModulesDictionaryQueryString = ({ app, lang }: IGetModulesDictionaryQuery) => {
    return qs.stringify(
        {
            app,
            lang,
        },
        { encode: false }
    );
};

export const getAggregatedDataQueryString = ({ type, id, period, timezone, reFetch, visibleClients, myTokensOnly }: IGetAggregatedDataQuery) => {
    return qs.stringify(
        {
            type,
            id,
            start: period.start,
            end: period.end,
            timezone: timezone,
            reFetch: !!reFetch,
            ...(visibleClients !== null &&
                visibleClients !== undefined && {
                    visibleClients,
                }),
            ...(myTokensOnly !== undefined && {
                myTokensOnly,
            }),
        },
        { encode: false }
    );
};
